import { useCallback, useRef } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useApolloClient, useQuery } from 'apollo-client'
import { useIntl } from 'intl'
import config from 'config'
import localStorage from 'local-storage'
import { sha256, decodeBase64 } from 'crypto-helpers'
import { CancelledPromiseError, constants, GraphQLError, ServerError, UserError } from 'helpers'
import links from 'links'
import { openModal } from 'modals'

import { useCotyAddressValidation, useShippingAddressAdd } from 'modules/user'
import type { PurchaseCommitErrorDetails } from 'typings/graphql'

import { buttonMessages } from 'components/inputs'

import purchaseBeginQuery, { type PurchaseBeginVariables } from './graph/purchaseBegin.graphql'
import purchaseCommitQuery, { type PurchaseCommitVariables } from './graph/purchaseCommit.graphql'
import purchaseWaitForCommitQuery from './graph/purchaseWaitForCommit.graphql'
import purchaseSessionQuery, { type PurchaseSessionVariables, type PurchaseSessionPayload } from './graph/purchaseSession.graphql'
import purchaseOneClickQuery, { type PurchaseOneClickVariables } from './graph/purchaseOneClick.graphql'
import purchaseInProgressQuery from './graph/purchaseInProgress.graphql'

import messages from './messages'


const openPurchaseOutOfStockModal = (errorDetails: PurchaseCommitErrorDetails[]) => {
  if (errorDetails) {
    const outOfStockItems = errorDetails
      .map(({ tradingItemName }) => tradingItemName)
      .join(', ')

    openModal('commonModal', {
      'data-testid': 'purchaseOutOfStockModal',
      title: messages.errors.outOfStock.title,
      text: { ...messages.errors.outOfStock.text, values: { outOfStockItems } },
      primaryButton: {
        title: buttonMessages.ok,
        to: links.subscription.main,
      },
    })
  }
}

const usePurchaseBeginMutation = () => {
  const apolloClient = useApolloClient()

  const validateCotyAddress = useCotyAddressValidation()
  const [ addShippingAddress ] = useShippingAddressAdd()
  const lastSessionIdRef = useRef<number>(null)

  const purchaseBegin = useCallback(async (input: PurchaseBeginVariables['input']) => {
    const result = await apolloClient.mutate({
      mutation: purchaseBeginQuery,
      variables: {
        input,
      },
      fetchPolicy: 'no-cache',
      context: {
        timeout: 0, // disable timeout
      },
    })

    // console.log('purchaseBegin', JSON.stringify(result))

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const error = result.data.purchaseBegin.error

    if (error) {
      if (error.__typename === 'ForbiddenProductsError' && error.forbiddenProductsErrorCode === 'FORBIDDEN_PRODUCTS') {
        const { products } = error

        const hasForbiddenProducts = await validateCotyAddress({ products })

        if (hasForbiddenProducts) {
          return purchaseBegin({ ...input, allowRemoveForbiddenProducts: true })
        }
      }

      if (error.__typename === 'PurchaseBeginError') {
        // begin has finished and we have saved sessionId, but the commit is failed for some reason
        // and user tries to begin again, but it fails with specific error codes indicating that transaction is finished or in progress
        if (
          lastSessionIdRef.current && (
            error.purchaseBeginErrorCode === 'CART_IS_EMPTY'
            || error.purchaseBeginErrorCode === 'ALREADY_SUBSCRIBED'
            || error.purchaseBeginErrorCode === 'ANOTHER_PURCHASE_IN_PROGRESS'
          )
        ) {
          // console.log('using previous sessionId')

          return {
            sessionId: null,
            v: null,
            existingSessionId: lastSessionIdRef.current,
          }
        }
        else {
          // reset in case of other error
          lastSessionIdRef.current = null
        }

        // when for some reason we don't have sessionId (user buys from multiple tabs),
        // we should load existing purchase session id and try to track it instead of ours
        if (error.purchaseBeginErrorCode === 'ANOTHER_PURCHASE_IN_PROGRESS') {
          const result = await apolloClient.query({
            query: purchaseInProgressQuery,
            fetchPolicy: 'no-cache',
          })

          // console.log('purchaseInProgress', JSON.stringify(result))

          const existingSessionId = result.data?.currentUser?.data?.purchaseSessionInfo?.lastPurchaseSessionIdInProgress

          if (existingSessionId) {
            return {
              sessionId: null,
              v: null,
              existingSessionId,
            }
          }
        }

        if (input.allowRemoveForbiddenProducts && error.purchaseBeginErrorCode === 'CART_IS_EMPTY') {
          await addShippingAddress({
            address: input.shipping,
            primary: false,
          })
        }
      }

      lastSessionIdRef.current = null
      throw new UserError(error)
    }

    lastSessionIdRef.current = result.data.purchaseBegin.data.sessionId

    return {
      ...result.data.purchaseBegin.data,
      existingSessionId: null,
    }
  }, [ apolloClient, validateCotyAddress, addShippingAddress ])

  return purchaseBegin
}

const usePurchaseCommitMutation = () => {
  const apolloClient = useApolloClient()
  const intl = useIntl()

  return useCallback(async (input: Exclude<PurchaseCommitVariables['input'], 'metadata'>) => {
    const lastProductMetadata = localStorage.getItem(constants.localStorageNames.lastAddedToCartProductMetadata) || {}
    const metadata = { isOneClick: false, ...lastProductMetadata }

    const result = await apolloClient.mutate({
      mutation: purchaseCommitQuery,
      variables: {
        input: {
          ...input,
          metadata,
        },
      },
      fetchPolicy: 'no-cache',
      context: {
        timeout: 0, // disable timeout
      },
    })

    // console.log('purchaseCommit', JSON.stringify(result))

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const error = result.data.purchaseCommit.error

    if (error) {
      if (error.__typename === 'PurchaseCommitError' && error.purchaseCommitErrorCode === 'PURCHASE_ITEMS_NOT_FOUND') {
        const { errorDetails } = error

        openPurchaseOutOfStockModal(errorDetails)

        // We need to invalidate the cart cache to remove products that are out of stock
        apolloClient.cache.evict({
          id: 'UserData:{}',
          fieldName: 'cart',
        })

        throw new CancelledPromiseError()
      }

      if (error.__typename === 'PaymentGatewayError' && error.paymentGatewayErrorCode === 'PURCHASE_TRANSACTION_ERROR' && input?.token?.paymentMethod === 'APPLE_PAY') {
        throw new UserError(error, intl.formatMessage(messages.errors.transactionError))
      }

      throw new UserError(error)
    }

    return result.data.purchaseCommit
  }, [ apolloClient ])
}

const usePurchaseOneClickMutation = () => {
  const apolloClient = useApolloClient()

  const validateCotyAddress = useCotyAddressValidation()

  const purchaseOneClick = useCallback(async (input: Exclude<PurchaseOneClickVariables['input'], 'metadata'>) => {
    const lastProductMetadata = localStorage.getItem(constants.localStorageNames.lastAddedToCartProductMetadata) || {}
    const metadata = { isOneClick: true, ...lastProductMetadata }

    const result = await apolloClient.mutate({
      mutation: purchaseOneClickQuery,
      variables: {
        input: {
          ...input,
          metadata,
        },
      },
      fetchPolicy: 'no-cache',
      context: {
        timeout: 0, // disable timeout
      },
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const error = result.data.purchaseOneClick.error

    if (error) {
      if (error.__typename === 'PurchaseCommitError' && error.purchaseCommitErrorCode === 'PURCHASE_ITEMS_NOT_FOUND') {
        const { errorDetails } = error

        openPurchaseOutOfStockModal(errorDetails)

        // We need to invalidate the cart cache to remove products that are out of stock
        apolloClient.cache.evict({
          id: 'UserData:{}',
          fieldName: 'cart',
        })

        throw new CancelledPromiseError()
      }

      if (error.__typename === 'ForbiddenProductsError' && error.forbiddenProductsErrorCode === 'FORBIDDEN_PRODUCTS') {
        const { products } = error

        const hasForbiddenProducts = await validateCotyAddress({ oneClickPurchase: true, products })

        if (hasForbiddenProducts) {
          return purchaseOneClick({ ...input, allowRemoveForbiddenProducts: true })
        }
      }

      throw new UserError(error)
    }

    return result.data.purchaseOneClick
  }, [ apolloClient, validateCotyAddress ])

  return purchaseOneClick
}

export const usePurchaseWaitForCommit = () => {
  const apolloClient = useApolloClient()
  const isWebsocketsEnabled = useFeatureIsOn(constants.features.paymentWebsockets)

  return useCallback((input: PurchaseSessionVariables['input']) => {
    return new Promise<PurchaseSessionPayload['purchaseSession']>((resolve, reject) => {
      const observableQuery = isWebsocketsEnabled
        ? apolloClient.subscribe({
          query: purchaseWaitForCommitQuery,
          fetchPolicy: 'network-only',
          variables: {
            input,
          },
          context: {
            webSocket: true,
          },
        })
        : apolloClient.watchQuery({
          query: purchaseSessionQuery,
          variables: {
            input,
          },
          fetchPolicy: 'no-cache',
          pollInterval: 1000,
        })

      // @ts-expect-error
      let subscription = observableQuery.subscribe({
        next: ({ data }) => {
          const { session, error } = data.purchaseSession || {}

          if (session?.status === 'SUCCESS') {
            endSubscription()
            resolve(data.purchaseSession)
          }
          else if (session?.status === 'FAILED' || !session && error) {
            endSubscription()
            reject(new UserError(error))
          }
        },
        error: (error) => {
          endSubscription()
          reject(new ServerError(error))
        },
        complete: () => {
          endSubscription()
        },
      })

      const endSubscription = () => {
        if (subscription) {
          subscription.unsubscribe()
          subscription = null
        }
      }
    })
  }, [ apolloClient, isWebsocketsEnabled ])
}

// it's a combination of commit and waitForCommit methods
const usePurchaseCommitWithWaiterMutation = () => {
  const commitMutation = usePurchaseCommitMutation()
  const waitForCommit = usePurchaseWaitForCommit()

  return useCallback(async (input: Exclude<PurchaseCommitVariables['input'], 'metadata'>) => {
    try {
      return await commitMutation(input)
    }
    catch (error) {
      const isPostponedPurchase = (
        // if it's a gateway timeout error, then we start waiting for the commit
        error?.networkError?.statusCode === 504
        // error code from the error. We use cause because all payment errors are wrapped in UserError
        || error?.cause?.purchaseErrorCode === 'EXTERNAL_NETWORK_ISSUE'
        || error?.cause?.purchaseErrorCode === 'PURCHASE_IN_PROGRESS'
        || error?.cause?.purchaseErrorCode === 'ALREADY_PROCESSED'
        || error?.cause?.purchaseErrorCode === 'UNKNOWN'
      )

      if (isPostponedPurchase) {
        return await waitForCommit({ sessionId: input.sessionId })
      }

      throw error
    }
  }, [ commitMutation, waitForCommit ])
}

// method for saving purchase session data to cache to skip re-fetching on thank you page
const useSavePurchaseSessionToCache = () => {
  const client = useApolloClient()

  return useCallback((input: PurchaseSessionVariables['input'], { data, session }: Pick<PurchaseSessionPayload['purchaseSession'], 'data' | 'session'>) => {
    client.cache.writeQuery({
      query: purchaseSessionQuery,
      variables: {
        input,
      },
      data: {
        purchaseSession: {
          '__typename': 'PurchaseCommitPayload',
          data,
          session,
          error: null,
        },
      },
    })
  }, [ client ])
}

export const usePurchaseSession = (input: PurchaseSessionVariables['input']) => {
  const { data, error, isFetching } = useQuery(purchaseSessionQuery, {
    variables: {
      input,
    },
    skip: !input.sessionId,
    fetchPolicy: 'cache-first',
  })

  return {
    session: data?.purchaseSession?.session,
    data: data?.purchaseSession?.data,
    error,
    isFetching,
  }
}

type PurchaseInput = PurchaseBeginVariables['input'] & {
  tokenId: PurchaseCommitVariables['input']['token']['tokenId']
}

// general purchase with payment method
export const usePurchase = () => {
  const beginMutation = usePurchaseBeginMutation()
  const commitMutation = usePurchaseCommitWithWaiterMutation()
  const waitForCommit = usePurchaseWaitForCommit()
  const saveToCache = useSavePurchaseSessionToCache()

  return useCallback(async (input: PurchaseInput) => {
    const { purchaseLevel, paymentMethod, shipping, billing, tokenId } = input

    const { sessionId, v, existingSessionId } = await beginMutation({
      purchaseLevel,
      paymentMethod,
      billing,
      shipping,
    })

    let data: PurchaseSessionPayload['purchaseSession']

    // if we have existing session in progress, we follow it
    if (existingSessionId) {
      data = await waitForCommit({ sessionId: existingSessionId })
    }
    else {
      // anti-fraud token
      const t = v ? await sha256(`${v}${decodeBase64(config.purchaseCommitSalt)}`) : undefined

      data = await commitMutation({
        sessionId,
        t,
        token: {
          tokenId,
          paymentMethod,
        },
      })
    }

    // save to skip re-fetching for thank you page
    if (data.session && data.data) {
      saveToCache({ sessionId: data.session.id }, data)
    }

    return data
  }, [ beginMutation, commitMutation, waitForCommit, saveToCache ])
}


// redeem without payment method
export const useRedeem = () => {
  const beginMutation = usePurchaseBeginMutation()
  const commitMutation = usePurchaseCommitWithWaiterMutation()
  const saveToCache = useSavePurchaseSessionToCache()

  return useCallback(async (input: Pick<PurchaseBeginVariables['input'], 'purchaseLevel' | 'shipping' | 'billing'>) => {
    const { purchaseLevel, shipping, billing } = input

    const { sessionId, v } = await beginMutation({
      purchaseLevel,
      billing,
      shipping,
    })

    // anti-fraud token
    const t = v ? await sha256(`${v}${decodeBase64(config.purchaseCommitSalt)}`) : undefined

    const data = await commitMutation({
      sessionId,
      t,
    })

    // save to skip re-fetching for thank you page
    if (data.session && data.data) {
      saveToCache({ sessionId: data.session.id }, data)
    }

    return data
  }, [ beginMutation, commitMutation, saveToCache ])
}


// pay in one click
export const usePurchaseInOneClick = () => {
  const purchaseOneClickMutation = usePurchaseOneClickMutation()
  const saveToCache = useSavePurchaseSessionToCache()

  return useCallback(async (input: Exclude<PurchaseOneClickVariables['input'], 'metadata'>) => {
    const data = await purchaseOneClickMutation(input)

    // save to skip re-fetching for thank you page
    if (data.session && data.data) {
      saveToCache({ sessionId: data.session.id }, data)
    }

    return data
  }, [ purchaseOneClickMutation, saveToCache ])
}
